import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useDisclosure } from '@chakra-ui/hooks';
import SuccessModal from '../myOffer/components/SuccessModal';
import {
    MdPhone,
    MdEmail,
    MdCheckCircle,
    MdHome,
    MdLanguage,
    MdBarChart,
    MdKeyboardArrowUp,
    MdKeyboardArrowDown,
    MdHourglassEmpty,
    MdWarning,
    MdCancel,
    MdVerified,
    MdTrendingUp,
    MdLeaderboard,
    MdThumbUp,
    MdLink,
} from 'react-icons/md';
import { FaMoneyBillWave, FaChartLine } from 'react-icons/fa';

// ─── Status helpers ────────────────────────────────────────────────────────────

type LeadStatus = {
    label: string;
    color: 'gray' | 'yellow' | 'orange' | 'green' | 'emerald' | 'red';
    icon: React.ReactNode;
    description: string;
};

// invalid / blocked / duplicate / test: refused by Leadrs before reaching a buyer (see refusalReasons).
const REFUSED_QUALITY_STATUSES = ['invalid', 'blocked', 'duplicate', 'test'];
// Leads received before leadQualityStatus existed only carry the orderNumber marker.
const LEGACY_REFUSED_ORDER_NUMBERS = ['INVALID', 'INVALID_SIDE_SELLER', 'BLOCKED', 'DUPLICATE', 'TEST'];
const isRefused = (l: any) =>
    l.leadQualityStatus
        ? REFUSED_QUALITY_STATUSES.includes(l.leadQualityStatus)
        : LEGACY_REFUSED_ORDER_NUMBERS.includes(l.orderNumber);

// NO_MATCH and its legacy variants (NO_MATCH_SIDE_SELLER, NO_MATCH-FAKER-n from seed scripts).
const hasNoMatchMarker = (l: any) => !l.orderNumber || String(l.orderNumber).startsWith('NO_MATCH');

// Still unsold. leadQualityStatus is the source of truth; the NO_MATCH / empty orderNumber
// marker only counts for legacy leads without it, and never once the lead is approved or paid.
const isNoMatch = (l: any) => {
    if (isRefused(l)) return false;
    if (l.leadQualityStatus) return l.leadQualityStatus === 'no_match';
    return hasNoMatchMarker(l) && l.approvalStatus !== 'approved' && !l.isPaid && !l.paidAt;
};

// Each lead belongs to exactly one category. The badge, the filter chips and their counters all
// read it from here, so a lead can never show up in two filters (e.g. paid AND approved).
// Order matters: Leadrs refusal > unsold > paid > approved > buyer rejection > under review.
// Paid wins over a buyer rejection: the seller really did receive the money in that case.
type LeadCategory = 'no_match' | 'unsold' | 'pending' | 'approved' | 'rejected' | 'paid';

// Still waiting for a buyer 7 days after its creation: the server marks it unsold (unsoldAt). It
// stays unsold only while no buyer takes it — a lead bought later reads as its new category.

// Legacy leads (before the approval workflow) have neither leadQualityStatus nor approvalStatus.
// Once their 48h window is over without a rejection, today's auto-approval cron would have approved
// them, so they read as approved. Refused / unsold legacy leads are caught earlier by their marker.
const isLegacyAutoApproved = (item: any) =>
    !item.leadQualityStatus && !item.approvalStatus && !!item.paymentEligibleAt && new Date(item.paymentEligibleAt) <= new Date();

const getLeadCategory = (item: any): LeadCategory => {
    if (isRefused(item)) return 'rejected';
    if (isNoMatch(item)) return item.unsoldAt ? 'unsold' : 'no_match';
    if (item.isPaid || item.paidAt) return 'paid';
    if (item.approvalStatus === 'approved' || isLegacyAutoApproved(item)) return 'approved';
    if (item.approvalStatus === 'rejected') return 'rejected';
    // pending, conflict, or no approvalStatus at all (legacy leads)
    return 'pending';
};

// Why a refused lead was refused, in French, whatever refused it (Leadrs or the buyer): the API
// sends a single refusalReasons list for both (see sellerRefusalMotifs). Same text on the card and
// in the expanded details.
const refusalMotif = (item: any): string | null =>
    getLeadCategory(item) === 'rejected' && item.refusalReasons?.length > 0 ? item.refusalReasons.join(' · ') : null;

const isSold = (item: any) => ['approved', 'paid'].includes(getLeadCategory(item));

const getLeadStatus = (item: any): LeadStatus => {
    switch (getLeadCategory(item)) {
        case 'no_match':
            return {
                label: 'En cours de traitement',
                color: 'gray',
                icon: <MdWarning size={15} />,
                description: 'Lead en cours de traitement, à la recherche d\'un acheteur',
            };
        case 'unsold':
            return {
                label: 'Invendu',
                color: 'gray',
                icon: <MdCancel size={15} />,
                description: 'Aucun acheteur trouvé sous 7 jours',
            };
        case 'paid':
            return {
                label: 'Payé',
                color: 'emerald',
                icon: <MdCheckCircle size={15} />,
                description: 'Le virement a été effectué',
            };
        case 'approved':
            return {
                label: 'Approuvé',
                color: 'green',
                icon: <MdVerified size={15} />,
                // A legacy lead has no price nor payment record: don't promise a transfer.
                description: isLegacyAutoApproved(item) ? 'Lead validé (délai de contestation de 48h écoulé)' : 'Lead validé, virement en cours',
            };
        case 'rejected':
            return {
                label: 'Refusé',
                color: 'red',
                icon: <MdCancel size={15} />,
                description: refusalMotif(item) || 'Lead non accepté',
            };
        case 'pending':
        default:
            return {
                label: 'Vérification',
                color: 'yellow',
                icon: <MdHourglassEmpty size={15} />,
                description: 'En attente de validation (48h)',
            };
    }
};

// Page (or site) that generated the lead, sent by the API as sourceUrl. Some are stored without
// protocol ("site.fr/page"): link them over https, display them without protocol nor trailing slash.
const sourceHref = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);
const sourceLabel = (url: string) => url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '');

// Product of the lead, sent by the API as { industry, subIndustry?, subIndustryId? }:
// "Pompe à chaleur (12)" — the sub-industry with its product id, or the industry alone.
const productLabel = (product: any) => {
    const name = product.subIndustry || product.industry;
    return product.subIndustryId != null ? `${name} (${product.subIndustryId})` : name;
};

// Same label in the card and the "Mission" filter: a purchase-order lead carries PO-<id>, shown as
// "Bon de commande" with the end of its id so two purchase orders can be told apart.
const orderNumberLabel = (orderNumber: string) =>
    orderNumber.startsWith('PO-') ? `Bon de commande #${orderNumber.slice(-6)}` : `Mission #${orderNumber}`;

// Mission the lead was delivered under, for the card's left column. Markers (NO_MATCH, INVALID,
// TEST…) are not missions.
const missionLabel = (item: any): string | null => {
    if (hasNoMatchMarker(item) || LEGACY_REFUSED_ORDER_NUMBERS.includes(item.orderNumber)) return null;
    return orderNumberLabel(String(item.orderNumber));
};

const STATUS_COLOR_CLASSES: Record<string, { badge: string; border: string; text: string }> = {
    gray:    { badge: 'bg-leadrs-bg text-leadrs-text-soft',          border: 'border-leadrs-stroke',        text: 'text-leadrs-text-soft'   },
    yellow:  { badge: 'bg-leadrs-yellow-soft text-leadrs-yellow-focus', border: 'border-leadrs-yellow-soft', text: 'text-leadrs-yellow-focus' },
    orange:  { badge: 'bg-leadrs-yellow-soft text-leadrs-yellow-focus', border: 'border-leadrs-yellow-soft', text: 'text-leadrs-yellow-focus' },
    green:   { badge: 'bg-leadrs-green-soft text-leadrs-green-focus',   border: 'border-leadrs-green-soft',  text: 'text-leadrs-green-focus'  },
    emerald: { badge: 'bg-leadrs-green-soft text-leadrs-green-focus',   border: 'border-leadrs-green-soft',  text: 'text-leadrs-green-focus'  },
    red:     { badge: 'bg-leadrs-red-soft text-leadrs-red-focus',       border: 'border-leadrs-red-soft',    text: 'text-leadrs-red-focus'    },
};


// ─── Filter options ────────────────────────────────────────────────────────────

const APPROVAL_STATUS_OPTIONS = [
    { value: '',           label: 'Tous les statuts' },
    { value: 'no_match',   label: 'En cours de traitement' },
    { value: 'unsold',     label: 'Invendu' },
    { value: 'pending',    label: 'Vérification' },
    { value: 'approved',   label: 'Approuvé' },
    { value: 'rejected',   label: 'Refusé' },
    { value: 'paid',       label: 'Payé' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const MyLeads = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    // Current page only: the server filters, paginates and counts (GET /lead/seller-page)
    const [leads, setLeads] = useState<any[]>([]);
    const [counts, setCounts] = useState<Record<string, number> | null>(null);
    const [missions, setMissions] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [isLoadingLeads, setIsLoadingLeads] = useState(true);
    const [templateNames, setTemplateNames] = useState<Record<string, string>>({});
    const [missionIdFilter, setMissionIdFilterState] = useState('');
    const [statusFilter, setStatusFilterState] = useState('');
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

    // Changing a filter always starts over from the first page
    const setStatusFilter = (value: string) => { setStatusFilterState(value); setPage(1); };
    const setMissionIdFilter = (value: string) => { setMissionIdFilterState(value); setPage(1); };
    const [period, setPeriod] = useState<'week' | 'month' | 'all'>('month');
    const [kpis, setKpis] = useState<{
        week:  { sent: number; verified: number; rejected: number; approved: number; acceptanceRate: number | null; revenue: number };
        month: { sent: number; verified: number; rejected: number; approved: number; acceptanceRate: number | null; revenue: number };
        all:   { sent: number; verified: number; rejected: number; approved: number; acceptanceRate: number | null; revenue: number };
    } | null>(null);

    const toggleCard = (leadId: string) => {
        setExpandedCards((prev) => {
            const next = new Set(prev);
            next.has(leadId) ? next.delete(leadId) : next.add(leadId);
            return next;
        });
    };

    const initUser = () => {
        const config = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
        axios.get(`${CONFIG.apiUrl}/user/me`, config).then((res) => {
            if (res.data && res.data.type !== 'seller') window.location.href = '/my-request';
        }).catch(() => {});
    };

    const PAGE_SIZE = 20;

    // Fetched with every page of leads, so the KPI cards never lag behind the filter chips
    const fetchKpis = () => {
        const config = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
        axios.get(`${CONFIG.apiUrl}/user/seller-lead-stats`, config)
            .then((res) => setKpis(res.data))
            .catch(() => {});
    };

    const fetchLeadsPage = () => {
        fetchKpis();
        const config = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
        const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
        if (statusFilter) params.set('status', statusFilter);
        if (missionIdFilter) params.set('mission', missionIdFilter);
        setIsLoadingLeads(true);
        axios.get(`${CONFIG.apiUrl}/lead/seller-page?${params.toString()}`, config)
            .then((res) => {
                setLeads(res.data.leads);
                setCounts(res.data.counts);
                setMissions(res.data.missions);
                setTotal(res.data.total);
                setTotalPages(res.data.totalPages);
            })
            .catch((error) => {
                if (error?.response?.status === 401) window.location.href = '/auth/sign-in';
            })
            .finally(() => setIsLoadingLeads(false));
    };

    const initMyLeads = () => {
        const config = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
        axios.get(`${CONFIG.apiUrl}/form-template`, config)
            .then((res) => {
                const map: Record<string, string> = {};
                (res.data || []).forEach((t: any) => { map[t._id] = t.name; });
                setTemplateNames(map);
            })
            .catch(() => {});
    };

    const clearFilter = () => {
        setMissionIdFilterState('');
        setStatusFilterState('');
        setPage(1);
    };

    useEffect(() => {
        fetchLeadsPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter, missionIdFilter]);

    useEffect(() => {
        if (!localStorage.getItem('token') || localStorage.getItem('token').length < 10) {
            window.location.href = '/auth/sign-up';
        }
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('created') === 'true' && !isOpen) onOpen();
        initMyLeads();
        initUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function timeAgo(date: any) {
        const diffInSeconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
        const days = Math.floor(diffInSeconds / 86400);
        const hours = Math.floor((diffInSeconds % 86400) / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        if (days > 0) return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
        if (hours > 0) return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
        if (minutes > 0) return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
        return `À l'instant`;
    }

    // ─── Sub-components ───────────────────────────────────────────────────────

    const HeaderCard = () => (
        <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">Vendeur</p>
                    <h1 className="mt-1 font-crimson text-4xl font-semibold leading-tight text-leadrs-text">Mes leads envoyés</h1>
                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                        Consultez et suivez le statut des leads que vous avez envoyés sur Leadrs.
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <label className="font-geist text-sm text-leadrs-text-soft whitespace-nowrap">Mission :</label>
                    <select
                        value={missionIdFilter}
                        onChange={(e) => setMissionIdFilter(e.target.value)}
                        className="rounded-lg border border-leadrs-stroke bg-white px-3 py-2 font-geist text-sm text-leadrs-text outline-none transition-colors focus:border-leadrs-brand"
                    >
                        <option value="">Toutes les missions</option>
                        {missions.map((id) => (
                            <option key={id} value={id}>{orderNumberLabel(id)}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    const KPICards = () => {
        const monthName = new Date().toLocaleDateString('fr-FR', { month: 'long' });
        const d = kpis ? kpis[period] : null;
        const periodLabel = period === 'week' ? 'cette semaine' : period === 'month' ? `en ${monthName}` : 'au total';

        const kpiList = [
            {
                label: 'Leads en vérification',
                value: countByStatus('pending'),
                icon: <MdHourglassEmpty size={22} />,
                iconBg: 'bg-yellow-100',
                iconColor: 'text-yellow-600',
                description: `En attente de validation (total actuel)`,
            },
            {
                label: `Leads acceptés`,
                value: d ? d.approved : '—',
                icon: <MdLeaderboard size={22} />,
                iconBg: 'bg-blue-100',
                iconColor: 'text-blue-600',
                description: `Leads acceptés par l'acheteur ${periodLabel}`,
            },
            {
                label: `Leads refusés`,
                value: d ? d.rejected : '—',
                icon: <MdCancel size={22} />,
                iconBg: 'bg-red-100',
                iconColor: 'text-red-600',
                description: `Leads refusés par l'acheteur ou par Leadrs ${periodLabel}`,
            },
            {
                label: `Taux d'acceptation`,
                value: d ? (d.acceptanceRate !== null ? `${d.acceptanceRate}%` : '—') : '—',
                icon: <MdThumbUp size={22} />,
                iconBg: 'bg-green-100',
                iconColor: 'text-green-600',
                description: `Approuvés / (approuvés + refusés par l'acheteur)`,
            },
        ];

        return (
            <div className="mt-5">
                {/* Period toggle */}
                <div className="mb-4 flex w-fit items-center gap-1 rounded-xl border border-leadrs-stroke bg-white p-1">
                    {(['week', 'month', 'all'] as const).map((p) => (
                        <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            className={`rounded-lg px-4 py-1.5 font-geist text-sm font-medium transition-colors ${
                                period === p ? 'bg-leadrs-brand text-white' : 'text-leadrs-text-soft hover:text-leadrs-text'
                            }`}
                        >
                            {p === 'week' ? 'Cette semaine' : p === 'month' ? 'Ce mois' : 'Depuis toujours'}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                    {kpiList.map((kpi, i) => (
                        <div key={i} className="rounded-xl border border-leadrs-stroke bg-white p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">
                                        {kpi.label}
                                    </p>
                                    <p className="mt-2 font-crimson text-3xl font-semibold text-leadrs-text">
                                        {kpi.value}
                                    </p>
                                </div>
                                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${kpi.iconBg} ${kpi.iconColor}`}>
                                    {kpi.icon}
                                </div>
                            </div>
                            <p className="mt-3 font-geist text-xs text-leadrs-text-soft">{kpi.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Totals over all the seller's leads, computed by the server whatever the current page
    const countByStatus = (value: string): number | string => {
        if (!counts) return '…';
        return value === '' ? counts.all : counts[value] ?? 0;
    };

    const FilterSection = () => (
        <div className="mt-5 flex flex-wrap gap-2">
            {APPROVAL_STATUS_OPTIONS.map((opt) => {
                const count = countByStatus(opt.value);
                const isActive = statusFilter === opt.value;
                return (
                    <button
                        key={opt.value}
                        onClick={() => setStatusFilter(opt.value)}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 font-geist text-sm transition-colors ${
                            isActive
                                ? 'border-leadrs-brand bg-leadrs-brand text-white'
                                : 'border-leadrs-stroke bg-white text-leadrs-text hover:border-leadrs-brand'
                        }`}
                    >
                        {opt.label}
                        <span className={`rounded-full px-1.5 py-0.5 text-xs font-bold ${
                            isActive ? 'bg-white/20 text-white' : 'bg-leadrs-bg text-leadrs-text-soft'
                        }`}>
                            {count}
                        </span>
                    </button>
                );
            })}
        </div>
    );

    const StatusBadge = ({ item }: { item: any }) => {
        const s = getLeadStatus(item);
        const c = STATUS_COLOR_CLASSES[s.color];
        return (
            <span
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${c.badge}`}
                title={s.description}
            >
                {s.icon}
                {s.label}
            </span>
        );
    };

    // ─── Main render ──────────────────────────────────────────────────────────

    return (
        <div className="flex flex-col gap-5">
            <SuccessModal isOpen={isOpen} onClose={onClose} />
            <HeaderCard />
            <KPICards />
            <FilterSection />

            {isLoadingLeads && leads.length === 0 ? (
                <p className="mt-10 text-center font-geist text-sm text-leadrs-text-soft">Chargement des leads…</p>
            ) : leads.length === 0 ? (
                <div className="mt-10 flex flex-col items-center gap-3">
                    <p className="font-geist text-sm text-leadrs-text-soft">
                        {counts?.all === 0 ? 'Aucun lead trouve' : 'Aucun lead ne correspond au filtre applique'}
                    </p>
                    {!!counts?.all && (missionIdFilter || statusFilter) && (
                        <button
                            onClick={clearFilter}
                            className="rounded-lg border border-leadrs-stroke px-4 py-2 font-geist text-sm text-leadrs-text transition-colors hover:border-leadrs-brand"
                        >
                            Effacer les filtres
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {leads.map((item) => {
                        const getField = (fieldName: string) =>
                            item.body?.find((e: any) => e.field === fieldName)?.answer || '';

                        const firstName    = getField('Prenom') || getField('Prénom') || getField('prenom') || getField('firstName');
                        const lastName     = getField('Nom') || getField('NOM') || getField('nom') || getField('lastName');
                        const phone        = getField('Telephone') || getField('Téléphone') || getField('telephone');
                        const email        = getField('Email') || getField('email');
                        const propertyType = getField('Type de bien');
                        const location     = getField('Localisation') || getField('Departement');
                        const iframeName = item.formTemplateId ? templateNames[item.formTemplateId] : null;
                        const isExpanded   = expandedCards.has(item._id);
                        const status       = getLeadStatus(item);
                        const colorClass   = STATUS_COLOR_CLASSES[status.color];

                        return (
                            <div
                                key={item._id}
                                onClick={() => toggleCard(item._id)}
                                className="cursor-pointer overflow-hidden rounded-xl border border-leadrs-stroke bg-white transition-shadow hover:shadow-md"
                            >
                                {/* Compact header */}
                                <div className="flex items-center justify-between gap-6 px-6 py-5">
                                    {/* Product + mission + time — the status lives on the right only */}
                                    <div className="w-[170px] shrink-0">
                                        {item.product ? (
                                            <div title={item.product.subIndustry ? `${item.product.industry} › ${productLabel(item.product)}` : item.product.industry}>
                                                {/* Long names wrap over up to 3 lines instead of being cut on one */}
                                                <p className="line-clamp-3 break-words font-geist text-sm font-semibold leading-snug text-leadrs-text">
                                                    {item.product.subIndustry || item.product.industry}
                                                </p>
                                                {item.product.subIndustryId != null && (
                                                    <p className="mt-0.5 font-geist text-[11px] text-leadrs-text-soft">
                                                        ID produit : {item.product.subIndustryId}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="font-geist text-sm italic text-leadrs-text-soft">Produit non renseigné</p>
                                        )}
                                        {missionLabel(item) && (
                                            <p className="mt-0.5 truncate font-geist text-xs text-leadrs-text-soft" title={item.orderNumber}>
                                                {missionLabel(item)}
                                            </p>
                                        )}
                                        <p className="mt-1 font-geist text-xs text-leadrs-text-soft">{timeAgo(item.createdAt)}</p>
                                    </div>

                                    {/* Contact */}
                                    <div className="flex flex-1 flex-col gap-1.5">
                                        <div className="flex items-baseline gap-2">
                                            <p className="font-geist text-sm font-semibold text-leadrs-text">
                                                {firstName || lastName
                                                    ? `${firstName} ${lastName}`.trim()
                                                    : <span className="text-leadrs-text-soft italic">Nom inconnu</span>
                                                }
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-4">
                                            {phone && (
                                                <div className="flex items-center gap-1.5 font-geist text-sm text-leadrs-text-soft">
                                                    <MdPhone size={14} />
                                                    <span>{phone}</span>
                                                </div>
                                            )}
                                            {email && (
                                                <div className="flex items-center gap-1.5 font-geist text-sm text-leadrs-text-soft">
                                                    <MdEmail size={14} />
                                                    <span>{email}</span>
                                                </div>
                                            )}
                                            {iframeName && (
                                                <div className="flex items-center gap-1.5 font-geist text-sm text-leadrs-text-soft">
                                                    <MdLanguage size={14} />
                                                    <span className="max-w-[160px] truncate">{`Iframe: ${iframeName}`}</span>
                                                </div>
                                            )}
                                            {item.sourceUrl && (
                                                <a
                                                    href={sourceHref(item.sourceUrl)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={`Page qui a généré le lead : ${item.sourceUrl}`}
                                                    className="flex items-center gap-1.5 font-geist text-sm text-leadrs-text-soft hover:text-leadrs-brand hover:underline"
                                                >
                                                    <MdLink size={14} />
                                                    <span className="max-w-[240px] truncate">{sourceLabel(item.sourceUrl)}</span>
                                                </a>
                                            )}
                                        </div>
                                        {refusalMotif(item) && (
                                            <p className="font-geist text-xs font-medium text-leadrs-red-focus">
                                                Motif : {refusalMotif(item)}
                                            </p>
                                        )}
                                        {/* Not sold yet: why, and the fix when it's the seller's to make
                                            (noMatchReason, see leadrs-back services/leadRefusalReason.ts). */}
                                        {item.noMatchReason && (
                                            <p className="font-geist text-xs text-leadrs-text-soft">
                                                {item.noMatchReason.label}
                                                {item.noMatchReason.code === 'unclassified_page' && item.formTemplateId && (
                                                    <>
                                                        {' '}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/integrations/${item.formTemplateId}?tab=installation&step=prices`);
                                                            }}
                                                            className="font-medium text-leadrs-brand hover:underline"
                                                        >
                                                            Indiquer l'activité →
                                                        </button>
                                                    </>
                                                )}
                                            </p>
                                        )}
                                        {item.formTemplateId && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/integrations/${item.formTemplateId}?tab=performances`);
                                                }}
                                                className="flex w-fit items-center gap-1 font-geist text-xs font-medium text-leadrs-brand hover:underline"
                                            >
                                                <MdBarChart size={13} />
                                                Voir les analytics
                                            </button>
                                        )}
                                        {item.channels && item.channels.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {item.channels.map((ch: string, i: number) => (
                                                    <span key={i} className="rounded border border-leadrs-stroke px-1.5 py-0.5 font-geist text-[10px] leading-tight text-leadrs-text">
                                                        {ch}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Quality score */}
                                    <div className="text-center">
                                        <p className="font-crimson text-2xl font-semibold text-leadrs-text">
                                            {item.leadrsRate != null ? (
                                                <>{item.leadrsRate}<span className="font-geist text-sm font-normal text-leadrs-text-soft">/10</span></>
                                            ) : '—'}
                                        </p>
                                        <p className="font-geist text-xs uppercase tracking-wide text-leadrs-text-soft">Qualité</p>
                                    </div>

                                    {/* Price + status */}
                                    <div className="flex items-center gap-3">
                                        {isSold(item) && item.leadPrice != null && (
                                            <div className="rounded-xl border border-leadrs-green-soft bg-leadrs-green-soft px-4 py-3 text-center">
                                                <p className="mb-1 font-geist text-[10px] font-bold uppercase text-leadrs-green-focus">Lead vendu</p>
                                                <p className="font-crimson text-2xl font-semibold text-leadrs-green-focus">{item.leadPrice.toFixed(2)}€</p>
                                            </div>
                                        )}
                                        <div className={`rounded-xl border bg-white px-4 py-3 text-center ${colorClass.border}`}>
                                            <p className={`mb-1 font-geist text-[10px] font-bold uppercase ${colorClass.text}`}>Statut</p>
                                            <div className={`flex items-center justify-center gap-1 font-geist text-sm font-semibold ${colorClass.text}`}>
                                                {status.icon}
                                                {status.label}
                                            </div>
                                        </div>
                                        <button className="flex h-8 w-8 items-center justify-center">
                                            {isExpanded
                                                ? <MdKeyboardArrowUp size={24} className="text-leadrs-text-soft" />
                                                : <MdKeyboardArrowDown size={24} className="text-leadrs-text-soft" />
                                            }
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded detail panel */}
                                {isExpanded && (
                                    <div className="border-t border-leadrs-stroke bg-leadrs-bg px-6 py-5">
                                        <div className="grid grid-cols-3 gap-6">
                                            <div>
                                                <p className="mb-3 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">Transaction</p>
                                                <div className="space-y-2">
                                                    {isSold(item) && item.leadPrice != null && (
                                                        <div className="flex justify-between">
                                                            <span className="font-geist text-sm text-leadrs-text-soft">Prix de vente :</span>
                                                            <span className="font-geist text-sm font-semibold text-leadrs-text">{item.leadPrice.toFixed(2)}€</span>
                                                        </div>
                                                    )}
                                                    <div className="flex justify-between">
                                                        <span className="font-geist text-sm text-leadrs-text-soft">Statut :</span>
                                                        <StatusBadge item={item} />
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="font-geist text-sm text-leadrs-text-soft">Date d&apos;envoi :</span>
                                                        <span className="font-geist text-sm font-semibold text-leadrs-text">{new Date(item.createdAt).toLocaleDateString('fr-FR')}</span>
                                                    </div>
                                                    {/* No paidAt (isPaid alone): the transfer date is unknown, updatedAt would be any later edit */}
                                                    {getLeadCategory(item) === 'paid' && item.paidAt && (
                                                        <div className="flex justify-between">
                                                            <span className="font-geist text-sm text-leadrs-text-soft">Date de virement :</span>
                                                            <span className="font-geist text-sm font-semibold text-leadrs-green-focus">{new Date(item.paidAt).toLocaleDateString('fr-FR')}</span>
                                                        </div>
                                                    )}
                                                    {refusalMotif(item) && (
                                                        <div className="mt-1 rounded-lg bg-leadrs-red-soft px-3 py-2 font-geist text-xs text-leadrs-red-focus">
                                                            <span className="font-semibold">Motif : </span>{refusalMotif(item)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <p className="mb-3 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">Source</p>
                                                <div className="space-y-2">
                                                    {iframeName && (
                                                        <div className="flex justify-between gap-2">
                                                            <span className="shrink-0 font-geist text-sm text-leadrs-text-soft">Source :</span>
                                                            <span className="max-w-[180px] truncate text-right font-geist text-sm font-semibold text-leadrs-text">{`Iframe: ${iframeName}`}</span>
                                                        </div>
                                                    )}
                                                    {item.sourceUrl && (
                                                        <div className="flex justify-between gap-2">
                                                            <span className="shrink-0 font-geist text-sm text-leadrs-text-soft">Page :</span>
                                                            <a
                                                                href={sourceHref(item.sourceUrl)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                title={item.sourceUrl}
                                                                className="max-w-[180px] truncate text-right font-geist text-sm font-semibold text-leadrs-brand hover:underline"
                                                            >
                                                                {sourceLabel(item.sourceUrl)}
                                                            </a>
                                                        </div>
                                                    )}
                                                    {/* Only meaningful while the buyer can still contest the lead */}
                                                    {getLeadCategory(item) === 'pending' && (
                                                        <div className="flex justify-between">
                                                            <span className="font-geist text-sm text-leadrs-text-soft">Délai d&apos;approbation :</span>
                                                            <span className="font-geist text-sm font-semibold text-leadrs-text">
                                                                {item.paymentEligibleAt && new Date(item.paymentEligibleAt) > new Date()
                                                                    ? `jusqu'au ${new Date(item.paymentEligibleAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`
                                                                    : '48h'}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {item.leadrsRate != null && (
                                                        <div className="flex justify-between">
                                                            <span className="font-geist text-sm text-leadrs-text-soft">Score qualité :</span>
                                                            <span className="font-geist text-sm font-semibold text-leadrs-text">{item.leadrsRate}/10</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <p className="mb-3 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">Informations</p>
                                                <div className="space-y-2">
                                                    {item.product && (
                                                        <div className="flex justify-between gap-2">
                                                            <span className="shrink-0 font-geist text-sm text-leadrs-text-soft">Produit :</span>
                                                            <span className="text-right font-geist text-sm font-semibold text-leadrs-text" title={item.product.industry}>
                                                                {productLabel(item.product)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {propertyType && (
                                                        <div className="flex justify-between">
                                                            <span className="font-geist text-sm text-leadrs-text-soft">Type de bien :</span>
                                                            <span className="font-geist text-sm font-semibold text-leadrs-text">{propertyType}</span>
                                                        </div>
                                                    )}
                                                    {location && (
                                                        <div className="flex justify-between">
                                                            <span className="font-geist text-sm text-leadrs-text-soft">Localisation :</span>
                                                            <span className="font-geist text-sm font-semibold text-leadrs-text">{location}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {leads.length > 0 && totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-leadrs-stroke bg-white px-4 py-3">
                    <button
                        onClick={() => { setPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        disabled={page <= 1 || isLoadingLeads}
                        className="rounded-lg border border-leadrs-stroke px-3 py-1.5 font-geist text-sm text-leadrs-text transition-colors hover:border-leadrs-brand disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        ← Précédent
                    </button>
                    <p className="font-geist text-sm text-leadrs-text-soft">
                        Page <span className="font-semibold text-leadrs-text">{page}</span> / {totalPages}
                        <span className="mx-2">·</span>
                        {total} lead{total > 1 ? 's' : ''}
                    </p>
                    <button
                        onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        disabled={page >= totalPages || isLoadingLeads}
                        className="rounded-lg border border-leadrs-stroke px-3 py-1.5 font-geist text-sm text-leadrs-text transition-colors hover:border-leadrs-brand disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Suivant →
                    </button>
                </div>
            )}
        </div>
    );
};

export default MyLeads;
